import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import A_TextField from "./A_TextField";

const useStyles = makeStyles(() => ({
  spinnerWrap: {
    display: "flex",
    flexDirection: "column",
    marginRight: -6,
  },
  spinnerBtn: {
    padding: 0,
    width: 20,
    height: 14,
    borderRadius: "calc(2px * var(--app-radius-scale, 1))",
    color: "var(--color-text-secondary)",
    "&:hover": {
      backgroundColor: "transparent",
      color: "var(--color-primary-text)",
    },
  },
  spinnerIcon: {
    fontSize: "1.125rem",
  },
}));

function formatCurrencyDisplay(raw) {
  if (raw === "" || raw === null || raw === undefined) return "";
  const str = String(raw).replace(/,/g, "");
  if (!str || str === "-") return str;
  const negative = str.startsWith("-");
  const abs = negative ? str.slice(1) : str;
  const dotIdx = abs.indexOf(".");
  const intStr = dotIdx === -1 ? abs : abs.slice(0, dotIdx);
  const decStr = dotIdx === -1 ? "" : abs.slice(dotIdx);
  const formattedInt = intStr
    ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : decStr
    ? "0"
    : "";
  return (negative ? "-" : "") + formattedInt + decStr;
}

function parseCurrencyInput(raw) {
  if (raw === null || raw === undefined) return "";
  return String(raw).replace(/[^0-9.-]/g, "");
}

function calcNewCursorPos(rawValue, cursorStart, formatted) {
  const charsBeforeCursor = rawValue
    .slice(0, cursorStart)
    .replace(/[^0-9.-]/g, "").length;
  let pos = formatted.length;
  let count = 0;
  for (let i = 0; i <= formatted.length; i++) {
    if (count >= charsBeforeCursor) {
      pos = i;
      break;
    }
    if (i < formatted.length && /[0-9.-]/.test(formatted[i])) {
      count++;
    }
  }
  return pos;
}

const A_CurrencyInput = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  currencySymbol,
  disabled,
  readOnly,
  InputProps: parentInputProps,
  ...rest
}) => {
  const classes = useStyles();
  const [displayValue, setDisplayValue] = useState(() =>
    formatCurrencyDisplay(value)
  );
  const isInternalChange = useRef(false);
  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    setDisplayValue(formatCurrencyDisplay(value));
  }, [value]);

  useLayoutEffect(() => {
    if (
      pendingCursorRef.current !== null &&
      inputRef.current &&
      document.activeElement === inputRef.current
    ) {
      const pos = pendingCursorRef.current;
      pendingCursorRef.current = null;
      inputRef.current.setSelectionRange(pos, pos);
    } else if (pendingCursorRef.current !== null) {
      pendingCursorRef.current = null;
    }
  }, [displayValue]);

  const handleChange = (event) => {
    const rawValue = event.target.value;
    const cursorStart = event.target.selectionStart;
    const stripped = parseCurrencyInput(rawValue);
    const formatted = formatCurrencyDisplay(stripped);
    pendingCursorRef.current = calcNewCursorPos(rawValue, cursorStart, formatted);
    isInternalChange.current = true;
    setDisplayValue(formatted);
    if (onChange) {
      onChange({
        ...event,
        target: { ...event.target, id: id || name, value: stripped },
      });
    }
  };

  const handleBlur = (event) => {
    if (onBlur) onBlur(event);
  };

  const handleIncrement = () => {
    const current = parseFloat(parseCurrencyInput(displayValue)) || 0;
    const newRaw = String(current + 1);
    isInternalChange.current = true;
    setDisplayValue(formatCurrencyDisplay(newRaw));
    if (onChange) {
      onChange({ target: { id: id || name, value: newRaw } });
    }
  };

  const handleDecrement = () => {
    const current = parseFloat(parseCurrencyInput(displayValue)) || 0;
    const newRaw = String(current - 1);
    isInternalChange.current = true;
    setDisplayValue(formatCurrencyDisplay(newRaw));
    if (onChange) {
      onChange({ target: { id: id || name, value: newRaw } });
    }
  };

  const spinnerButtons =
    !disabled && !readOnly ? (
      <div className={classes.spinnerWrap}>
        <IconButton
          className={classes.spinnerBtn}
          onClick={handleIncrement}
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
          disableRipple
        >
          <ArrowDropUpIcon className={classes.spinnerIcon} />
        </IconButton>
        <IconButton
          className={classes.spinnerBtn}
          onClick={handleDecrement}
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
          disableRipple
        >
          <ArrowDropDownIcon className={classes.spinnerIcon} />
        </IconButton>
      </div>
    ) : null;

  const mergedInputProps = {
    ...(parentInputProps || {}),
    endAdornment: (
      <>
        {parentInputProps?.endAdornment}
        {spinnerButtons}
      </>
    ),
  };

  return (
    <A_TextField
      {...rest}
      id={id}
      name={name}
      disabled={disabled}
      readOnly={readOnly}
      inputRef={inputRef}
      InputProps={mergedInputProps}
      defaultValue={displayValue}
      inputType="value"
      onChange={handleChange}
      onBlur={handleBlur}
      adornment={{ position: "start", value: currencySymbol }}
    />
  );
};

A_CurrencyInput.defaultProps = {
  value: "",
  currencySymbol: "£",
  onChange: null,
  onBlur: null,
  disabled: false,
  readOnly: false,
  InputProps: undefined,
};

A_CurrencyInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currencySymbol: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  InputProps: PropTypes.object,
};

export default A_CurrencyInput;
